// frontend/src/pages/NotesFrais.js
// Notes de frais admin — scan OCR (OCR.space gratuit) + gestion tickets + génération PDF mensuel
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useIsMobile } from '../hooks/useIsMobile';

const API_URL     = process.env.REACT_APP_API_URL     || 'http://localhost:5000/api';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const CATEGORIES = ['Transport', 'Repas', 'Hébergement', 'Fournitures', 'Télécom', 'Carburant', 'Divers'];

const theme = {
  primary: '#1a3a5c', primaryBg: '#e8f0fe', primaryLight: '#4a7bb5',
  success: '#16a34a', successBg: '#f0fdf4',
  danger:  '#dc2626', dangerBg:  '#fef2f2',
  warning: '#d97706', warningBg: '#fffbeb',
  info:    '#0369a1', infoBg:    '#e0f2fe',
  border: '#e5e7eb', pageBg: '#f5f7fb',
  textPrimary: '#1a1a2e', textSecondary: '#4f6d89', textMuted: '#9ca3af',
  gradientHeader: 'linear-gradient(135deg, #1a3a5c 0%, #2d6a9f 100%)',
  shadow: '0 2px 8px rgba(0,0,0,0.08)',
};

const fmt     = v => new Intl.NumberFormat('fr-BE', { style: 'currency', currency: 'EUR' }).format(v || 0);
const dateStr = d => d ? new Date(d).toLocaleDateString('fr-BE') : '—';
const today       = () => new Date().toISOString().split('T')[0];
const firstOfMonth = () => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; };

const EMPTY_FORM = {
  date_ticket: today(), emetteur: '', montant_ht: '',
  montant_tva: '', montant_ttc: '', taux_tva: '21',
  categorie: 'Divers', notes: '',
  image_path: '', image_filename: '', ocr_status: 'manual', ocr_raw: null,
};

export default function NotesFrais() {
  const isMobile = useIsMobile();
  const token  = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const [tickets,    setTickets]    = useState([]);
  const [notes,      setNotes]      = useState([]);
  const [view,       setView]       = useState('tickets'); // 'tickets' | 'notes'
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [showForm,   setShowForm]   = useState(false);
  const [editId,     setEditId]     = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrText,    setOcrText]    = useState('');     // texte brut OCR pour debug
  const [genLoading, setGenLoading] = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [periode,    setPeriode]    = useState({ from: firstOfMonth(), to: today() });
  const [preview,    setPreview]    = useState(null);
  const [toast,      setToast]      = useState(null);   // { msg, type }
  const fileRef   = useRef();  // file picker
  const cameraRef = useRef();  // input capture=environment (mobile)
  const videoRef  = useRef();  // getUserMedia stream (MacBook/desktop)
  const streamRef = useRef(null);
  const [showCamera, setShowCamera] = useState(false);

  // ── Toast auto-dismiss ────────────────────────────────────────────────────
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/notes-frais/tickets`, config);
      setTickets(r.data?.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token]);

  const fetchNotes = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/notes-frais`, config);
      setNotes(r.data?.data || []);
    } catch (e) {}
  }, [token]);

  useEffect(() => { fetchTickets(); fetchNotes(); }, [fetchTickets, fetchNotes]);

  // ── Camera Handler : getUserMedia (MacBook) / capture native (mobile) ──────
  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      fileRef.current?.click(); // fallback file picker
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: isMobile ? 'environment' : 'user',
                 width: { ideal: 1920 }, height: { ideal: 1080 } },
      });
      streamRef.current = stream;
      setShowCamera(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 80);
    } catch (err) {
      console.warn('[Camera]', err.message);
      fileRef.current?.click(); // permission refusée → file picker
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const v      = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width  = v.videoWidth  || 1280;
    canvas.height = v.videoHeight || 720;
    canvas.getContext('2d').drawImage(v, 0, 0);
    canvas.toBlob(blob => {
      if (!blob) return;
      const file = new File([blob], `scan-${Date.now()}.jpg`, { type: 'image/jpeg' });
      stopCamera();
      handleOCR(file);
    }, 'image/jpeg', 0.92);
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setShowCamera(false);
  };

  // ── OCR Handler ───────────────────────────────────────────────────────────
  const handleOCR = async (file) => {
    if (!file) return;
    setOcrLoading(true);
    setOcrText('');
    setPreview(URL.createObjectURL(file));
    setShowForm(false);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await axios.post(`${API_URL}/notes-frais/tickets/ocr`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      const d = r.data?.data || {};
      if (d.ocr_text) setOcrText(d.ocr_text);
      setForm(prev => ({
        ...prev,
        date_ticket:    d.date_ticket   || prev.date_ticket,
        emetteur:       d.emetteur      || prev.emetteur,
        montant_ht:     d.montant_ht   != null ? String(d.montant_ht)  : prev.montant_ht,
        montant_tva:    d.montant_tva  != null ? String(d.montant_tva) : prev.montant_tva,
        montant_ttc:    d.montant_ttc  != null ? String(d.montant_ttc) : prev.montant_ttc,
        taux_tva:       d.taux_tva     != null ? String(d.taux_tva)    : prev.taux_tva,
        image_path:     d.image_path    || prev.image_path,
        image_filename: d.image_filename || prev.image_filename,
        ocr_status:     d.ocr_status    || 'done',
        ocr_raw:        d.ocr_raw       || null,
      }));
      setShowForm(true);
      if (d.ocr_status === 'error') {
        showToast('OCR partiellement échoué — vérifiez et complétez les champs.', 'warning');
      } else {
        showToast('✅ OCR réussi — vérifiez les champs extraits.', 'success');
      }
    } catch (e) {
      showToast('OCR échoué: ' + (e.response?.data?.message || e.message) + ' — saisissez manuellement.', 'danger');
      setShowForm(true);
    }
    setOcrLoading(false);
  };

  // ── Save ticket ───────────────────────────────────────────────────────────
  const saveTicket = async () => {
    if (!form.montant_ttc || !form.date_ticket) {
      showToast('Date et montant TTC sont obligatoires.', 'danger');
      return;
    }
    try {
      if (editId) {
        await axios.put(`${API_URL}/notes-frais/tickets/${editId}`, form, config);
        showToast('Ticket mis à jour.', 'success');
      } else {
        await axios.post(`${API_URL}/notes-frais/tickets`, form, config);
        showToast('Ticket enregistré.', 'success');
      }
      setShowForm(false); setEditId(null); setForm(EMPTY_FORM); setPreview(null); setOcrText('');
      fetchTickets();
    } catch (e) { showToast('Erreur: ' + (e.response?.data?.message || e.message), 'danger'); }
  };

  // ── Auto-calc TVA depuis TTC ──────────────────────────────────────────────
  const calcFromTTC = (ttc, tauxStr) => {
    const taux = parseFloat(tauxStr || 21);
    const ht   = ttc / (1 + taux / 100);
    const tva  = ttc - ht;
    return { ht: ht.toFixed(2), tva: tva.toFixed(2) };
  };

  // ── Générer note de frais ──────────────────────────────────────────────────
  const genererNote = async () => {
    if (!window.confirm(`Générer une note de frais du ${dateStr(periode.from)} au ${dateStr(periode.to)} ?`)) return;
    setGenLoading(true);
    try {
      const r = await axios.post(`${API_URL}/notes-frais/generer`,
        { periode_debut: periode.from, periode_fin: periode.to }, config);
      const d = r.data.data;
      showToast(`✅ Note ${d.numero} générée — ${d.nb_tickets} ticket(s) — TTC ${fmt(d.montant_ttc_total)}`, 'success');
      fetchTickets(); fetchNotes(); setView('notes');
    } catch (e) { showToast('Erreur: ' + (e.response?.data?.message || e.message), 'danger'); }
    setGenLoading(false);
  };

  // ── Suppression note de frais ─────────────────────────────────────────────
  const deleteNote = async (id, numero) => {
    if (!window.confirm(`Supprimer la note ${numero} ?\nLes tickets seront remis disponibles.`)) return;
    try {
      await axios.delete(`${API_URL}/notes-frais/${id}`, config);
      showToast(`Note ${numero} supprimée — tickets remis disponibles.`, 'success');
      fetchNotes();
      fetchTickets();
    } catch (e) {
      showToast('Erreur suppression : ' + (e.response?.data?.message || e.message), 'danger');
    }
  };

  // ── Téléchargement PDF authentifié ───────────────────────────────────────
  const downloadPdf = async (id, numero) => {
    try {
      const r = await fetch(`${API_URL}/notes-frais/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`Erreur ${r.status}`);
      const blob = await r.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `${numero}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast('Erreur téléchargement PDF : ' + e.message, 'danger');
    }
  };

  // ── Totaux période ─────────────────────────────────────────────────────────
  const ticketsPeriode = tickets.filter(t => {
    if (!t.date_ticket) return false;
    const d = t.date_ticket.slice(0, 10);
    return d >= periode.from && d <= periode.to && !t.id_note_frais;
  });
  const totalHTp  = ticketsPeriode.reduce((s, t) => s + parseFloat(t.montant_ht  || 0), 0);
  const totalTVAp = ticketsPeriode.reduce((s, t) => s + parseFloat(t.montant_tva || 0), 0);
  const totalTTCp = ticketsPeriode.reduce((s, t) => s + parseFloat(t.montant_ttc || 0), 0);

  // ── Styles ─────────────────────────────────────────────────────────────────
  const S = {
    page:    { minHeight: '100vh', background: theme.pageBg, fontFamily: 'system-ui,sans-serif' },
    header:  { background: theme.gradientHeader, padding: isMobile ? '14px' : '20px 28px', color: 'white' },
    content: { maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '14px 10px' : '24px' },
    card:    { background: 'white', borderRadius: '12px', boxShadow: theme.shadow, padding: '20px', marginBottom: '16px' },
    input:   { width: '100%', padding: '9px 12px', border: `1px solid ${theme.border}`,
               borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '10px',
               outline: 'none', fontFamily: 'system-ui,sans-serif' },
    btn:        { padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                  fontWeight: '600', fontSize: '13px' },
    btnPrimary: { background: theme.primary, color: 'white', padding: '10px 20px', borderRadius: '8px',
                  border: 'none', cursor: 'pointer', fontWeight: '700', fontSize: '14px' },
    btnGreen:   { background: theme.successBg, color: theme.success, border: `1px solid #86efac`,
                  padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '13px' },
    btnRed:     { background: theme.dangerBg, color: theme.danger, padding: '6px 12px',
                  borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '12px' },
    label:      { fontSize: '11px', fontWeight: '600', color: theme.textSecondary,
                  textTransform: 'uppercase', display: 'block', marginBottom: '4px' },
  };

  const toastColors = {
    success: { bg: '#f0fdf4', border: '#86efac', color: '#15803d' },
    warning: { bg: '#fffbeb', border: '#fcd34d', color: '#92400e' },
    danger:  { bg: '#fef2f2', border: '#fca5a5', color: '#b91c1c' },
  };

  return (
    <div style={S.page}>

      {/* ── Toast notification ── */}
      {toast && (
        <div style={{
          position: 'fixed', top: '16px', right: '16px', zIndex: 9999,
          background: toastColors[toast.type]?.bg || '#f0fdf4',
          border: `1px solid ${toastColors[toast.type]?.border || '#86efac'}`,
          color: toastColors[toast.type]?.color || '#15803d',
          borderRadius: '10px', padding: '12px 18px', maxWidth: 'calc(100vw - 32px)',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', fontSize: '13px', fontWeight: '600',
          animation: 'fadeIn 0.2s ease',
        }}>
          {toast.msg}
        </div>
      )}

      {/* ── Header ── */}
      <div style={S.header}>
        <h1 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', fontWeight: '700' }}>
          🧾 Notes de Frais
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: '13px', opacity: 0.8 }}>
          Scan OCR · Tickets · Génération mensuelle PDF
        </p>
      </div>

      <div style={S.content}>

        {/* ── Tabs ── */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {[['tickets', '🧾 Mes tickets'], ['notes', '📄 Mes notes de frais']].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} style={{
              ...S.btn,
              background: view === k ? theme.primary : 'white',
              color:      view === k ? 'white' : theme.primary,
              border:     `1px solid ${view === k ? theme.primary : theme.border}`,
            }}>{l}</button>
          ))}
          {notes.length > 0 && (
            <span style={{ alignSelf: 'center', marginLeft: 'auto', fontSize: '12px',
              color: theme.textMuted, background: theme.primaryBg, borderRadius: '20px',
              padding: '4px 12px' }}>
              {notes.length} note{notes.length > 1 ? 's' : ''} générée{notes.length > 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* ══════════════════════════════════════════════════════════════
            VUE TICKETS
        ══════════════════════════════════════════════════════════════ */}
        {view === 'tickets' && (<>

          {/* ── Zone scan + saisie manuelle ── */}
          <div style={{ ...S.card, borderLeft: `4px solid #7c3aed` }}>
            <h3 style={{ margin: '0 0 12px', color: theme.primary, fontSize: '15px' }}>
              ➕ Ajouter un ticket de caisse ou une facture
            </h3>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* 📷 Scanner — getUserMedia MacBook / capture native mobile */}
              <button
                onClick={() => isMobile ? cameraRef.current?.click() : openCamera()}
                disabled={ocrLoading}
                style={{ ...S.btnPrimary, background: '#7c3aed', display: 'flex', alignItems: 'center', gap: '6px',
                  opacity: ocrLoading ? 0.7 : 1 }}>
                {ocrLoading
                  ? <><span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⏳</span>&nbsp;Analyse OCR…</>
                  : '📷 Scanner ticket (OCR)'}
              </button>
              {/* 📁 Fichier — toujours disponible */}
              <button
                onClick={() => fileRef.current?.click()}
                disabled={ocrLoading}
                style={{ ...S.btn, background: 'white', border: '1px solid #7c3aed', color: '#7c3aed' }}>
                📁 Fichier / Galerie
              </button>
              <button
                onClick={() => { setForm(EMPTY_FORM); setPreview(null); setEditId(null); setOcrText(''); setShowForm(true); }}
                style={{ ...S.btn, background: 'white', border: `1px solid ${theme.border}`, color: theme.text }}>
                ✏️ Saisie manuelle
              </button>
              {/* Input mobile : capture=environment → caméra arrière native */}
              <input ref={cameraRef} type="file" accept="image/*" capture="environment"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) handleOCR(e.target.files[0]); e.target.value = ''; }}
              />
              {/* Input file picker (galerie/documents) */}
              <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,application/pdf"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) handleOCR(e.target.files[0]); e.target.value = ''; }}
              />
            </div>
            <p style={{ margin: '10px 0 0', fontSize: '11px', color: theme.textMuted }}>
              📡 OCR via <strong>OCR.space</strong> (service gratuit — 500 scans/jour) ·
              Formats : JPG, PNG, WEBP, HEIC, PDF · Max 5 Mo
            </p>
          </div>

          {/* ── Formulaire ticket ── */}
          {showForm && (
            <div style={{ ...S.card, border: `2px solid ${theme.primary}`, animation: 'fadeIn 0.2s ease' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <h3 style={{ margin: 0, color: theme.primary, fontSize: '15px' }}>
                  {editId ? '✏️ Modifier le ticket' : '🧾 Nouveau ticket'}
                </h3>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  {form.ocr_status === 'done' && (
                    <span style={{ fontSize: '11px', background: theme.successBg, color: theme.success,
                      padding: '3px 10px', borderRadius: '10px', fontWeight: '600' }}>
                      ✅ OCR réussi — vérifiez
                    </span>
                  )}
                  {form.ocr_status === 'error' && (
                    <span style={{ fontSize: '11px', background: theme.warningBg, color: theme.warning,
                      padding: '3px 10px', borderRadius: '10px', fontWeight: '600' }}>
                      ⚠️ OCR partiel — complétez
                    </span>
                  )}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 2fr', gap: '0 20px' }}>
                {/* Colonne gauche: image */}
                <div>
                  {preview && (
                    <div style={{ marginBottom: '14px' }}>
                      <img src={preview} alt="Ticket scanné"
                        style={{ maxHeight: '220px', maxWidth: '100%', borderRadius: '8px',
                          objectFit: 'contain', border: `1px solid ${theme.border}`, display: 'block' }} />
                      <p style={{ margin: '6px 0 0', fontSize: '10px', color: theme.textMuted }}>
                        {form.image_filename || 'Ticket scanné'}
                      </p>
                    </div>
                  )}
                  {/* Texte OCR brut (debug) */}
                  {ocrText && (
                    <div style={{ background: '#f8fafc', borderRadius: '6px', padding: '8px',
                      fontSize: '10px', color: '#64748b', maxHeight: '100px', overflow: 'auto',
                      border: `1px solid ${theme.border}`, marginBottom: '12px' }}>
                      <strong style={{ display: 'block', marginBottom: '4px' }}>Texte OCR brut :</strong>
                      {ocrText.substring(0, 400)}{ocrText.length > 400 ? '…' : ''}
                    </div>
                  )}
                </div>

                {/* Colonne droite: formulaire */}
                <div>
                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 14px' }}>
                    <div>
                      <label style={S.label}>Date *</label>
                      <input type="date" value={form.date_ticket}
                        onChange={e => setForm(p => ({ ...p, date_ticket: e.target.value }))}
                        style={S.input} />
                    </div>
                    <div>
                      <label style={S.label}>Catégorie</label>
                      <select value={form.categorie}
                        onChange={e => setForm(p => ({ ...p, categorie: e.target.value }))}
                        style={S.input}>
                        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label style={S.label}>Émetteur (société / magasin)</label>
                    <input type="text" value={form.emetteur} placeholder="ex: Delhaize, Total, SNCB..."
                      onChange={e => setForm(p => ({ ...p, emetteur: e.target.value }))}
                      style={S.input} />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0 14px' }}>
                    <div>
                      <label style={S.label}>Montant TTC * (€)</label>
                      <input type="number" step="0.01" value={form.montant_ttc}
                        onChange={e => {
                          const ttc = parseFloat(e.target.value || 0);
                          const { ht, tva } = calcFromTTC(ttc, form.taux_tva);
                          setForm(p => ({ ...p, montant_ttc: e.target.value, montant_ht: ht, montant_tva: tva }));
                        }}
                        style={{ ...S.input, borderColor: !form.montant_ttc ? '#fca5a5' : theme.border }} />
                    </div>
                    <div>
                      <label style={S.label}>Taux TVA (%)</label>
                      <select value={form.taux_tva}
                        onChange={e => {
                          const ttc = parseFloat(form.montant_ttc || 0);
                          const { ht, tva } = calcFromTTC(ttc, e.target.value);
                          setForm(p => ({ ...p, taux_tva: e.target.value, montant_ht: ht, montant_tva: tva }));
                        }}
                        style={S.input}>
                        {['0', '6', '12', '21'].map(t => <option key={t} value={t}>{t}%</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={S.label}>Montant HT (€) — calculé</label>
                      <input type="number" step="0.01" value={form.montant_ht}
                        onChange={e => setForm(p => ({ ...p, montant_ht: e.target.value }))}
                        style={{ ...S.input, background: '#f9fafb', color: theme.textSecondary }} />
                    </div>
                    <div>
                      <label style={S.label}>TVA (€) — calculée</label>
                      <input type="number" step="0.01" value={form.montant_tva}
                        onChange={e => setForm(p => ({ ...p, montant_tva: e.target.value }))}
                        style={{ ...S.input, background: '#f9fafb', color: theme.textSecondary }} />
                    </div>
                  </div>

                  <div>
                    <label style={S.label}>Notes (optionnel)</label>
                    <input type="text" value={form.notes} placeholder="Commentaire libre..."
                      onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
                      style={S.input} />
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: '4px', flexWrap: 'wrap' }}>
                    <button onClick={saveTicket} style={S.btnPrimary}>
                      {editId ? '💾 Mettre à jour' : '✅ Enregistrer le ticket'}
                    </button>
                    <button onClick={() => { setShowForm(false); setEditId(null); setForm(EMPTY_FORM); setPreview(null); setOcrText(''); }}
                      style={{ ...S.btn, background: '#f3f4f6', color: theme.textSecondary }}>
                      Annuler
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Zone génération note de frais ── */}
          <div style={{ ...S.card, background: theme.warningBg, border: `1px solid #fde68a` }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '14px', color: '#92400e' }}>
              📄 Générer une note de frais — sélectionner la période
            </h3>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={{ ...S.label, color: '#92400e' }}>Du</label>
                <input type="date" value={periode.from}
                  onChange={e => setPeriode(p => ({ ...p, from: e.target.value }))}
                  style={{ ...S.input, width: 'auto', marginBottom: 0 }} />
              </div>
              <div>
                <label style={{ ...S.label, color: '#92400e' }}>Au</label>
                <input type="date" value={periode.to}
                  onChange={e => setPeriode(p => ({ ...p, to: e.target.value }))}
                  style={{ ...S.input, width: 'auto', marginBottom: 0 }} />
              </div>
              <button onClick={genererNote}
                disabled={genLoading || ticketsPeriode.length === 0}
                style={{
                  ...S.btnPrimary,
                  background: ticketsPeriode.length ? '#d97706' : '#e5e7eb',
                  color:      ticketsPeriode.length ? 'white'   : '#9ca3af',
                  opacity:    genLoading ? 0.6 : 1,
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                {genLoading
                  ? '⏳ Génération…'
                  : `📄 + Note de frais (${ticketsPeriode.length} ticket${ticketsPeriode.length !== 1 ? 's' : ''})`}
              </button>
            </div>
            {ticketsPeriode.length > 0 && (
              <div style={{ marginTop: '12px', display: 'flex', gap: '20px', flexWrap: 'wrap',
                fontSize: '12px', color: '#92400e' }}>
                <span>HT : <strong>{fmt(totalHTp)}</strong></span>
                <span>TVA : <strong>{fmt(totalTVAp)}</strong></span>
                <span style={{ fontWeight: '700', fontSize: '14px' }}>TTC : <strong>{fmt(totalTTCp)}</strong></span>
                <span style={{ marginLeft: 'auto', opacity: 0.7 }}>
                  {ticketsPeriode.filter(t => t.image_path).length} justificatif(s) annexé(s)
                </span>
              </div>
            )}
            {ticketsPeriode.length === 0 && (
              <p style={{ margin: '10px 0 0', fontSize: '12px', color: '#92400e', opacity: 0.7 }}>
                Aucun ticket libre sur cette période. Ajustez les dates ou ajoutez des tickets.
              </p>
            )}
          </div>

          {/* ── Liste de tous les tickets ── */}
          <div style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: theme.primary }}>
                🧾 Tous les tickets ({tickets.length})
              </h3>
              <div style={{ display: 'flex', gap: '8px', fontSize: '11px', color: theme.textMuted, flexWrap: 'wrap' }}>
                <span style={{ background: theme.successBg, color: theme.success,
                  padding: '2px 8px', borderRadius: '10px' }}>
                  ✅ {tickets.filter(t => t.ocr_status === 'done').length} OCR
                </span>
                <span style={{ background: '#f3f4f6', color: theme.textMuted,
                  padding: '2px 8px', borderRadius: '10px' }}>
                  ✏️ {tickets.filter(t => t.ocr_status === 'manual').length} manuel
                </span>
                <span style={{ background: theme.successBg, color: theme.success,
                  padding: '2px 8px', borderRadius: '10px' }}>
                  📎 {tickets.filter(t => t.id_note_frais).length} dans NDF
                </span>
              </div>
            </div>

            {loading ? (
              <p style={{ color: theme.textMuted, textAlign: 'center', padding: '30px' }}>Chargement…</p>
            ) : tickets.length === 0 ? (
              <p style={{ color: theme.textMuted, textAlign: 'center', padding: '40px' }}>
                Aucun ticket. Utilisez "📷 Scanner ticket" ou "✏️ Saisie manuelle".
              </p>
            ) : isMobile ? (
              <div style={{ display:'flex', flexDirection:'column', gap:'8px', padding:'4px 0' }}>
                {tickets.map(t => (
                  <div key={t.id_ticket} style={{
                    background:'white', borderRadius:'10px', padding:'11px 13px',
                    boxShadow:'0 1px 4px rgba(0,0,0,0.07)',
                    borderLeft:'3px solid ' + (t.id_note_frais ? theme.success : theme.primary),
                    opacity: t.id_note_frais ? 0.7 : 1 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'5px' }}>
                      <div>
                        <div style={{ fontSize:'12px', color:theme.textSecondary }}>{dateStr(t.date_ticket)}</div>
                        <span style={{ background:theme.primaryBg, color:theme.primary,
                          padding:'2px 7px', borderRadius:'10px', fontSize:'10px',
                          display:'inline-block', marginTop:'3px' }}>
                          {t.categorie}
                        </span>
                      </div>
                      <div style={{ textAlign:'right' }}>
                        <div style={{ fontWeight:'800', fontSize:'16px', color:theme.primary }}>{fmt(t.montant_ttc)} €</div>
                        <div style={{ fontSize:'10px', color:theme.textSecondary }}>HT {fmt(t.montant_ht)} · TVA {fmt(t.montant_tva)}</div>
                      </div>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                      <div style={{ fontSize:'12px', fontWeight:'600', color:theme.primary,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'55%' }}>
                        {t.emetteur || '—'}
                      </div>
                      <div style={{ fontSize:'11px' }}>
                        {t.id_note_frais
                          ? <span style={{ color:theme.success }}>✅ NDF inclus</span>
                          : t.ocr_status === 'done'
                            ? <span style={{ color:theme.success }}>✅ OCR</span>
                            : t.ocr_status === 'error'
                              ? <span style={{ color:theme.warning }}>⚠️ OCR KO</span>
                              : <span style={{ color:theme.textSecondary }}>✏️ Manuel</span>}
                      </div>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <div>
                        {t.image_path
                          ? <a href={`${BACKEND_URL}${t.image_path}`} target="_blank" rel="noreferrer"
                              style={{ color:'#7c3aed', textDecoration:'none', fontSize:'11px', fontWeight:'600' }}>
                              📎 {(t.image_original_name || 'ticket').substring(0, 14)}
                            </a>
                          : <span style={{ fontSize:'11px', color:theme.textSecondary }}>—</span>}
                      </div>
                      {!t.id_note_frais && (
                        <div style={{ display:'flex', gap:'5px' }}>
                          <button onClick={() => {
                            setForm({ ...EMPTY_FORM, ...t,
                              taux_tva:    String(t.taux_tva    || 21),
                              montant_ht:  String(t.montant_ht  || ''),
                              montant_tva: String(t.montant_tva || ''),
                              montant_ttc: String(t.montant_ttc || ''),
                            });
                            setEditId(t.id_ticket);
                            setPreview(t.image_path ? `${BACKEND_URL}${t.image_path}` : null);
                            setOcrText(''); setShowForm(true); window.scrollTo(0, 200);
                          }} style={{ ...S.btn, background:theme.primaryBg, color:theme.primary, padding:'4px 8px', fontSize:'11px' }}>✏️</button>
                          <button onClick={async () => {
                            if (!window.confirm('Supprimer ce ticket et son image ?')) return;
                            await axios.delete(`${API_URL}/notes-frais/tickets/${t.id_ticket}`, config);
                            showToast('Ticket supprimé.', 'success'); fetchTickets();
                          }} style={S.btnRed}>🗑️</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '580px' }}>
                  <thead>
                    <tr style={{ background: '#f1f5f9' }}>
                      {['Date', 'Émetteur', 'Catégorie', 'HT', 'TVA', 'TTC', 'Annexe', 'Statut', ''].map(h => (
                        <th key={h} style={{ padding: '9px 10px', textAlign: 'left',
                          fontSize: '11px', fontWeight: '700', color: theme.textSecondary,
                          textTransform: 'uppercase', borderBottom: `2px solid ${theme.border}` }}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((t, i) => (
                      <tr key={t.id_ticket} style={{
                        background: i % 2 === 0 ? 'white' : '#fafafa',
                        borderBottom: `1px solid ${theme.border}`,
                        opacity: t.id_note_frais ? 0.55 : 1,
                      }}>
                        <td style={{ padding: '9px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                          {dateStr(t.date_ticket)}
                        </td>
                        <td style={{ padding: '9px 10px', fontSize: '12px', fontWeight: '600',
                          maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.emetteur || '—'}
                        </td>
                        <td style={{ padding: '9px 10px', fontSize: '12px' }}>
                          <span style={{ background: theme.primaryBg, color: theme.primary,
                            padding: '2px 7px', borderRadius: '10px', fontSize: '10px', whiteSpace: 'nowrap' }}>
                            {t.categorie}
                          </span>
                        </td>
                        <td style={{ padding: '9px 10px', fontSize: '12px' }}>{fmt(t.montant_ht)}</td>
                        <td style={{ padding: '9px 10px', fontSize: '12px' }}>{fmt(t.montant_tva)}</td>
                        <td style={{ padding: '9px 10px', fontSize: '13px', fontWeight: '700', color: theme.primary }}>
                          {fmt(t.montant_ttc)}
                        </td>
                        <td style={{ padding: '9px 10px', fontSize: '11px' }}>
                          {t.image_path ? (
                            <a href={`${BACKEND_URL}${t.image_path}`} target="_blank" rel="noreferrer"
                              style={{ color: '#7c3aed', textDecoration: 'none', fontWeight: '600' }}
                              title={t.image_original_name || t.image_path}>
                              📎 {(t.image_original_name || 'ticket').substring(0, 12)}…
                            </a>
                          ) : (
                            <span style={{ color: theme.textMuted }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '9px 10px', fontSize: '11px' }}>
                          {t.id_note_frais
                            ? <span style={{ color: theme.success, whiteSpace: 'nowrap' }}>✅ NDF inclus</span>
                            : t.ocr_status === 'done'
                              ? <span style={{ color: theme.success }}>✅ OCR</span>
                              : t.ocr_status === 'error'
                                ? <span style={{ color: theme.warning }}>⚠️ OCR KO</span>
                                : <span style={{ color: theme.textMuted }}>✏️ Manuel</span>}
                        </td>
                        <td style={{ padding: '9px 10px' }}>
                          {!t.id_note_frais && (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button onClick={() => {
                                setForm({
                                  ...EMPTY_FORM, ...t,
                                  taux_tva:    String(t.taux_tva    || 21),
                                  montant_ht:  String(t.montant_ht  || ''),
                                  montant_tva: String(t.montant_tva || ''),
                                  montant_ttc: String(t.montant_ttc || ''),
                                });
                                setEditId(t.id_ticket);
                                setPreview(t.image_path ? `${BACKEND_URL}${t.image_path}` : null);
                                setOcrText('');
                                setShowForm(true);
                                window.scrollTo(0, 200);
                              }} style={{ ...S.btn, background: theme.primaryBg, color: theme.primary,
                                padding: '4px 8px', fontSize: '11px' }}>
                                ✏️
                              </button>
                              <button onClick={async () => {
                                if (!window.confirm('Supprimer ce ticket et son image ?')) return;
                                await axios.delete(`${API_URL}/notes-frais/tickets/${t.id_ticket}`, config);
                                showToast('Ticket supprimé.', 'success');
                                fetchTickets();
                              }} style={S.btnRed}>
                                🗑️
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>)}

        {/* ══════════════════════════════════════════════════════════════
            VUE NOTES DE FRAIS
        ══════════════════════════════════════════════════════════════ */}
        {view === 'notes' && (
          <div style={S.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
              <h3 style={{ margin: 0, fontSize: '15px', color: theme.primary }}>
                📄 Mes notes de frais ({notes.length})
              </h3>
              <button onClick={() => setView('tickets')}
                style={{ ...S.btn, background: '#d97706', color: 'white', fontSize: '13px' }}>
                + Note de frais
              </button>
            </div>

            {notes.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '50px 20px', color: theme.textMuted }}>
                <div style={{ fontSize: '40px', marginBottom: '12px' }}>📄</div>
                <p style={{ fontSize: '14px', margin: 0 }}>
                  Aucune note générée.<br />
                  Allez dans "🧾 Mes tickets" et cliquez "+ Note de frais".
                </p>
              </div>
            ) : (
              notes.map(n => (
                <div key={n.id_note_frais} style={{
                  padding: '16px', marginBottom: '12px',
                  border: `1px solid ${theme.border}`,
                  borderRadius: '12px',
                  borderLeft: `5px solid ${theme.warning}`,
                  background: theme.warningBg,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '16px', color: theme.primary }}>
                        {n.numero}
                      </div>
                      <div style={{ fontSize: '12px', color: theme.textSecondary, marginTop: '3px' }}>
                        📅 {dateStr(n.periode_debut)} → {dateStr(n.periode_fin)}
                        &nbsp;·&nbsp; 🧾 {n.nb_tickets} ticket{n.nb_tickets !== 1 ? 's' : ''}
                        &nbsp;·&nbsp; Générée le {dateStr(n.date_generation)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '20px', fontWeight: '800', color: theme.primary }}>
                        {fmt(n.montant_ttc_total)}
                      </div>
                      <div style={{ fontSize: '11px', color: theme.textMuted }}>
                        HT: {fmt(n.montant_ht_total)} · TVA: {fmt(n.montant_tva_total)}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: '12px', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button onClick={() => downloadPdf(n.id_note_frais, n.numero)}
                      style={{ ...S.btn, background: '#92400e', color: 'white',
                        display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                      📄 Télécharger PDF
                    </button>
                    <span style={{
                      fontSize: '11px', padding: '3px 10px', borderRadius: '10px', fontWeight: '600',
                      background: n.statut === 'generee' ? theme.successBg : '#f3f4f6',
                      color:      n.statut === 'generee' ? theme.success    : theme.textMuted,
                    }}>
                      {n.statut === 'generee' ? '✅ Générée' : n.statut}
                    </span>
                    <button onClick={() => deleteNote(n.id_note_frais, n.numero)}
                      style={{ ...S.btnRed, marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                      🗑️ Supprimer
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

      </div>

      {/* ── CSS animations globales ── */}
      <style>{`
        @keyframes fadeIn  { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin    { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
      {/* ── Modal Caméra Desktop/MacBook (getUserMedia) ────────────────── */}
      {showCamera && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999,
        }}>
          <div style={{
            background: '#1a1a2e', borderRadius: '16px', padding: '20px',
            maxWidth: '680px', width: '95%',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0, color: 'white', fontSize: '15px' }}>📷 Scanner le ticket</h3>
              <button onClick={stopCamera}
                style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white',
                  borderRadius: '50%', width: '32px', height: '32px', cursor: 'pointer', fontSize: '16px' }}>
                ✕
              </button>
            </div>
            <video ref={videoRef} autoPlay playsInline muted
              style={{ width: '100%', borderRadius: '10px', background: '#000',
                maxHeight: '400px', objectFit: 'cover' }}
            />
            <div style={{ display: 'flex', gap: '12px', marginTop: '14px', justifyContent: 'center' }}>
              <button onClick={capturePhoto}
                style={{ background: '#7c3aed', color: 'white', border: 'none',
                  borderRadius: '50px', padding: '11px 30px', fontSize: '15px',
                  cursor: 'pointer', fontWeight: '700', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📸 Capturer
              </button>
              <button onClick={stopCamera}
                style={{ background: 'rgba(255,255,255,0.1)', color: 'white', border: 'none',
                  borderRadius: '50px', padding: '11px 22px', fontSize: '14px', cursor: 'pointer' }}>
                Annuler
              </button>
            </div>
            <p style={{ margin: '10px 0 0', color: 'rgba(255,255,255,0.4)', fontSize: '11px', textAlign: 'center' }}>
              Positionnez le ticket face à la caméra · Cliquez Capturer pour analyser via OCR.space
            </p>
          </div>
        </div>
      )}

    </div>
  );
}
